'use client';

import Button from '@/components/common/Button/Button';
import Image from 'next/image';
import PriveeWordmarkWhite from 'public/img/logo/reserve-logo-white.svg';
import PriveeKeyholeWhite from 'public/img/icon/keyhole-white.png';

import './PriveeLoggedInInvitedNotActive.style.scss';
import { useModal } from '@/components/hook/modal';
import AcceptInvitationModal from '../AcceptInvitationModal/PriveeAcceptInvitationModal';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import Text from '@/components/common/Text/Text';
import {
  FallbackSubscriptionPrices,
  SubscriptionProduct
} from '@/types/Subscription';
import { useFlags } from '@/components/hook/flags';
import { FLAG_KEYS } from '@/const/flags';
import { useEffect } from 'react';

type Props = {
  subscriptionProducts: SubscriptionProduct[];
};

function PriveeLoggedInInvitedNotActive({ subscriptionProducts }: Props) {
  const { showModal, closeModal } = useModal();
  const { value: flagValues } = useFlags();

  const priveePriceAsStr = (
    subscriptionProducts.find((sp) => sp.name.toLowerCase().includes('privee'))
      ?.price.asFormattedString ??
    FallbackSubscriptionPrices.Privee.asFormattedString
  ).slice(0, -3);

  const shouldShowPriveeInitiationFee =
    flagValues[FLAG_KEYS.PRIVEE_CHARGE_INITIATION_FEE];
  const initiationFeeAsStr = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  })
    .format(Number(FallbackSubscriptionPrices.PriveeInitiationFee))
    .slice(0, -3);

  const handleAcceptInvitationClick = () => {
    showModal(
      <AcceptInvitationModal
        priveePriceStr={priveePriceAsStr}
        priveeInitFeeStr={
          shouldShowPriveeInitiationFee ? initiationFeeAsStr : null
        }
        onClose={() => closeModal()}
      />
    );
  };

  useEffect(() => {
    // Close AcceptInvitationModal upon unmount
    return () => {
      closeModal();
    };
  }, []);

  return (
    <div className='privee-invited-container'>
      <div className='hero-container'>
        <div className='hero-title-container'>
          <div className='hero-title-line'>
            <h1 className='hero-title-text hero-title-the-word-your'>
              <span className='hero-title-y-in-your'>Y</span>our
            </h1>
            <Image
              className='privee-wordmark'
              src={PriveeWordmarkWhite}
              alt='Réservé Wordmark'
              width={127.081}
              height={54.31}
            />
          </div>
          <div className='hero-title-line'>
            <h1 className='hero-title-text'>
              Invitation&nbsp;
              <span className='hero-title-the-word-awaits'>
                <span className='hero-title-a-in-awaits'>A</span>waits
              </span>
            </h1>
          </div>
        </div>
        <Text
          variant='text-m'
          className='hero-description'
        >
          You&apos;ve been selected for R&eacute;serv&eacute; — a private tier
          reserved for a select circle of Vivrelle&nbsp;members.
          <br />
          <br className='newline-hide-on-desktop' />
          Enjoy elevated access, dedicated concierge{' '}
          <br className='newline-hide-on-desktop newline-hide-below-xs' />
          service, and the rarest pieces in&nbsp;rotation.
        </Text>
        <Button
          variant='light'
          size='small'
          className='accept-invitation-button'
          onClick={handleAcceptInvitationClick}
        >
          Accept Invitation
        </Button>
        <Image
          className='privee-keyhole'
          src={PriveeKeyholeWhite}
          alt='Réservé Keyhole in white'
          width={892}
          height={1814}
        />
      </div>
      <div className='about-container'>
        <h2 className='about-title'>About R&eacute;serv&eacute;</h2>
        <Text
          variant='text-m'
          className='about-description'
        >
          Réservé is an invite-only add-on tier within Vivrelle that gives you
          access to an exclusive collection of ultra high-end luxury
          accessories, including Hermès Birkin and Kelly bags, as well as
          premium styles from Van Cleef & Arpels, the Bvlgari Serpenti
          collection, and more. Members can borrow one Réservé item at a time
          with a 30-day swap cycle, supported by a dedicated VIP concierge who
          handles every detail — from sourcing specific colors and leathers to
          providing purchase quotes at special members-only pricing if you fall
          in love, with a portion of your membership fees applied towards your
          purchase.
        </Text>
      </div>
      <div className='membership-details-container'>
        <h2 className='membership-details-title'>Membership Details</h2>
        <div className='membership-details-prices'>
          <Text
            variant='text-m'
            className='membership-details-prices-item'
          >
            <span className='membership-details-prices-item-label'>
              Monthly R&eacute;serv&eacute; Add-On
            </span>
            :&nbsp;{priveePriceAsStr} per month
          </Text>
          {shouldShowPriveeInitiationFee && (
            <Text
              variant='text-m'
              className='membership-details-prices-item'
            >
              <span className='membership-details-prices-item-label'>
                One-Time Initiation Fee
              </span>
              :&nbsp;{initiationFeeAsStr}
            </Text>
          )}
        </div>
        <Text
          variant='text-m'
          className='membership-details-description'
        >
          R&eacute;serv&eacute; is an add-on tier and requires an active
          Vivrelle membership. Upon accepting your invitation,{' '}
          {shouldShowPriveeInitiationFee &&
            'the one-time initiation fee will be charged and '}
          your monthly R&eacute;serv&eacute; membership will begin immediately.
          Membership renews automatically each month and may be canceled anytime
          in accordance with Vivrelle membership&nbsp;terms.
        </Text>
        <Link
          href='https://www.vivrelle.com/terms-reserve'
          className='membership-details-link'
          target='_blank'
        >
          View Full Terms & Conditions
        </Link>
        <Button
          variant='light'
          size='small'
          className='accept-invitation-button'
          onClick={handleAcceptInvitationClick}
        >
          Accept Invitation
        </Button>
      </div>
      <Text className='contact-concierge'>
        Have questions? Contact your R&eacute;serv&eacute; concierge at{' '}
        <a
          className='contact-concierge-mailto-link'
          href='mailto:vip@vivrelle.com'
        >
          vip@vivrelle.com
        </a>
      </Text>
    </div>
  );
}

export default PriveeLoggedInInvitedNotActive;
